import React, {useState, useEffect} from "react";
import {useChat} from '../../hooks/useChat'
import { usePolling } from "../../hooks/usePolling";
import { useUser } from '../../contexts/UserContext';
import ConversationSidebar from "./ConversationSidebar";
import ChatWindow from "./ChatWindow";
import NewConversationModal from "./NewConversationModal";
import ContactInfoModal from "./ContactInfoModal";
import ConfirmModal from "./ConfirmModal";
import LoadingSpinner from "../UI/LoadingSpinner";
import '../ChatContainer.css';

const ChatContainer = () => {
    const { user } = useUser();
    const [showNewConversationModal,setShowNewConversationModal] = useState(false);
    const [showContactInfoModal, setShowContactInfoModal] = useState(false);
    const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const {
        conversations,
        activeConversation,
        messages,
        loading,
        unreadCount,
        loadConversations,
        selectConversation,
        sendMessage,
        searchConversations,
        sendFile,
        createConversation,
        attendConversation,
        closeConversation,
        loadUnreadCount,
        refreshMessages,
        possibleClients,
        assignClientToConversation,
        searchClientsByPhone,
        plantillas,
        loadPlantillas,
        sendTemplate
    } = useChat();

    // Cargar conversaciones al iniciar
    useEffect(() => {
        loadConversations();
        loadUnreadCount();
        loadPlantillas();
    }, []);

        useEffect(() => {
        const delay = setTimeout(() => {
            if (searchTerm.trim() === '') {
                loadConversations();
            } else {
                searchConversations(searchTerm);
            }
        }, 400); // 400ms debounce

        return () => clearTimeout(delay);
    }, [searchTerm]);

        useEffect(() => {
            const delay = setTimeout(() => {
                if (searchTerm.trim().length < 2) {
                    // Si no hay texto o es muy corto, carga conversaciones normales
                    loadConversations();
                } else {
                    // Si hay texto, buscar en backend
                    searchConversations(searchTerm);
                }
            }, 400); // debounce 400ms

            return () => clearTimeout(delay);
        }, [searchTerm]);

    // Polling cada 10 segundos para mensajes más frecuentes
    usePolling(()=>{
    if (!searchTerm.trim()) {
        loadConversations();
        loadUnreadCount();
    }
        // Si hay una conversación activa, recargar sus mensajes
        if (activeConversation?.id) {
            refreshMessages();
        }
    }, [activeConversation?.id, searchTerm], 10000);

    const handleSendMessage = async (content) => {
        try {
            await sendMessage(content, user);
        } catch (error) {
            alert('Error enviando mensaje');
        }
    };

    const handleSendFile = async (file, caption) => {
        try {
            await sendFile(file, caption);
        } catch (error) {
            alert(`Error enviando archivo: ${error.message}`);
            throw error;
        }
    };

    const handleSendTemplate = async (nombrePlantilla, variables) => {
        await sendTemplate(nombrePlantilla, variables);
    };

    const handleCreateConversation = async (phoneNumber, contactName, clientId = null) => {
        try {
            await createConversation(phoneNumber, contactName, clientId);
        } catch (error) {
            alert('Error creando conversación');
        }
    };

    const handleCloseConversation = async () => {
        setShowConfirmCloseModal(true);
    };
    
    const handleAssignClient = async (clientId) => {
        try {
            await assignClientToConversation(activeConversation.id, clientId);
            await loadConversations(); // 🔥 refresca lista
            await selectConversation(activeConversation.id); // 🔥 refresca activa

            setShowContactInfoModal(false);
        } catch (error) {
            console.error("Error al asignar el cliente:", error);
            alert(`Hubo un error al asignar el cliente: ${error.message}`);
        }
    };

    const confirmCloseConversation = async () => {
        try {
            await closeConversation();
            setShowConfirmCloseModal(false);
        } catch (error) {
            alert('Error cerrando conversación');
        }
    };


    const handleSelectConversation = async (conversationId) => {
        const conv = conversations.find(c => c.id === conversationId);
        if (!conv) return;

        await selectConversation(conversationId);

        if (
            (conv.estado?.toLowerCase() === "activa" ||
            conv.estado?.toLowerCase() === "bot") &&
            !conv.atendidaPor
        ) {
            await attendConversation(conv);
        }
    };

    if (loading && conversations.length === 0){
        return (
            <div className="right_col" role="main" style={{ height: 'calc(100vh - 100px)', padding: '0' }}>
                <div className="chat-container-wrapper">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <LoadingSpinner size="large"/>
                    </div>
                </div>
            </div>
        );
    }

    return(
        <div className="right_col" role="main" style={{ height: 'calc(100vh - 100px)', padding: '0' }}>
            <div className="chat-container-wrapper">
                <div className="chat-container">
                    {/* Panel izquierdo - Lista de conversaciones */}
                    <div className="chat-sidebar">
                        <ConversationSidebar
                            conversations={conversations || []}
                            activeConversationId={activeConversation?.id}
                            onSelectConversation={handleSelectConversation}
                            onNewConversation={() => setShowNewConversationModal(true)}
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}        
                        />
                    </div>
                    
                    {/* Panel derecho - Chat activo */}
                    <div className="chat-main">
                        {activeConversation ? (
                            <ChatWindow
                                conversation={activeConversation}
                                messages={messages}
                                onSendMessage={handleSendMessage}
                                onSendFile={handleSendFile}
                                // onAttendConversation={attendConversation}
                                onAttendConversation={() => attendConversation(activeConversation)}
                                onCloseConversation={handleCloseConversation}
                                onShowContactInfo={() => setShowContactInfoModal(true)}
                                plantillas={plantillas}
                                onSendTemplate={handleSendTemplate}
                            />
                        ) : (
                            <div className="no-chat-selected">
                                <i className="fa fa-comments" style={{ fontSize: '72px', marginBottom: '24px', opacity: 0.3 }}></i>
                                <h2>Selecciona una conversación</h2>
                                <p>Elige un chat de la lista o inicia una nueva conversación</p>
                            </div>
                        )}
                    </div>
                    
                    <NewConversationModal
                        isOpen={showNewConversationModal}
                        onClose={() => setShowNewConversationModal(false)}
                        onCreateConversation={handleCreateConversation}
                        onSearchClientsByPhone={searchClientsByPhone}
                    />
                    
                    <ContactInfoModal
                        isOpen={showContactInfoModal}
                        onClose={() => setShowContactInfoModal(false)}
                        conversation={activeConversation}
                        onAssignClient={handleAssignClient}
                    />
                    
                    <ConfirmModal
                        isOpen={showConfirmCloseModal}
                        onClose={() => setShowConfirmCloseModal(false)}
                        onConfirm={confirmCloseConversation}
                        title="Cerrar Conversación"
                        message="¿Estás seguro de que deseas cerrar esta conversación?"
                    />
                </div>
            </div>
        </div>
    );
};
export default ChatContainer;